import {
  BooleanOrNullOrUndefined,
  DateOrNullOrUndefined,
  NumberOrNullOrUndefined,
  StringOrNullOrUndefined,
} from 'src/app/shared/utils';
import type { NoteChargeStatus, NoteRbtV2 } from './note.rbt.v2.model';
import type { NoteCombinedV2 } from './note-combined.v2.model';
import { ProviderV2 } from './provider.v2.model';
import { PatientV2 } from './patient.v2.model';
import {
  AssessmentToolType,
  AssessmentToolStage,
  CaregiverGoalProtocol,
  DiscussedPlanProtocol,
  PlanProtocol,
  Protocol,
  RbtGoalProtocol,
  TeacherGoalProtocol,
} from './notes.v2.model';

type NoteStatus = 'pending' | 'ok' | 'no';
export class NoteBcbaV2 {
  type!: 'bcba';
  id!: number;
  bip_id!: number;
  client_id!: number;
  patient_id!: number;
  patient_name?: string;
  insurance_identifier!: string;
  patient_identifier!: string;
  participants!: string;
  environmental_changes!: string;

  doctor_id!: number;
  patient_code!: string;
  provider_signature!: string;
  provider_name!: number;
  provider_id!: number;
  provider_credential!: string;
  supervisor_signature!: string;
  supervisor_name!: number;
  supervisor_id!: number;

  session_date!: string | Date;
  time_in!: string;
  time_out!: string;
  time_in2!: string;
  time_out2!: string;
  session_length_total!: number;
  total_hours!: string;
  total_minutes!: number;
  total_units!: number;
  unit_price!: number | null;
  total_charges!: number | null;
  stored_total_units!: number | null;
  stored_unit_price!: number | null;
  stored_total_charges!: number | null;
  charges_complete!: boolean;
  charge_status!: NoteChargeStatus;
  configured_unit_price!: number | null;
  charges_calculated_at!: string | Date | null;
  charges_locked!: boolean;

  pos!: string;
  pa_service_id!: number;
  is_telehealth?: boolean;

  note_description!: string;
  location_id!: number;
  summary_note!: string;
  birth_date!: string | Date;
  aba_supervisor!: number;
  cpt_code!: string;
  diagnosis_code!: string;
  rendering_provider!: number;
  billed_blocked!: boolean;
  billed!: boolean;
  paid!: boolean;
  bcba!: string;
  md!: string;
  md2!: string;
  md3!: string;
  modifiers!: string;
  valid_modifiers?: { modifier: string; unit_price: number }[];
  meet_with_client_at!: string;
  pos_description!: string | null;
  status!: NoteStatus;

  created_at!: Date;
  updated_at!: Date;
  deleted_at!: Date;

  // 97151
  subtype?: AssessmentToolType;
  stage?: AssessmentToolStage;
  BCBA_conducted_assessments?: boolean;
  BCBA_conducted_client_observations?: boolean;
  assessment_tools?: string[];
  intake_outcome?: string[];

  // 97155
  was_the_rbt_present?: boolean;
  maladaptives?: MaladaptivesData;
  replacements?: ReplacementsData;
  rbt_training_goals!: RbtTrainingGoals;
  intervention_protocols?: Protocol[];
  replacement_protocols?: PlanProtocol[];
  other_protocol?:boolean;
  details_other_protocol?:string;
  modifications_needed_at_this_time?: boolean;
  additional_goals_or_interventions?: string;
  why_was_the_protocol_modified_or_not_modified?:string;
  describe_in_detail_the_protocol_modifications?:string;

  // 97156
  behavior_protocols?: DiscussedPlanProtocol[]; //tambien en la nota 55
  caregiver_goals?: CaregiverGoalProtocol[];
  teacher_goals?: TeacherGoalProtocol[];

  insurance_id!: number;
  provider?: ProviderV2;
  patient?: PatientV2;

  progress_noted_this_session_compared_to_previous_session?:string; // to note 97155 and 97156

  constructor(data: Partial<NoteBcbaV2> = {}) {
    const result = {
      ...data,
      type: 'bcba',
      id: NumberOrNullOrUndefined(data.id),
      bip_id: NumberOrNullOrUndefined(data.bip_id),
      patient_id: NumberOrNullOrUndefined(data.patient_id),
      patient_name: StringOrNullOrUndefined(data.patient_name),
      insurance_identifier: StringOrNullOrUndefined(data.insurance_identifier),
      client_id:
        NumberOrNullOrUndefined(data.client_id) ??
        NumberOrNullOrUndefined(data.patient_id),
      participants: StringOrNullOrUndefined(data.participants),
      environmental_changes: StringOrNullOrUndefined(
        data.environmental_changes
      ),

      patient_code: StringOrNullOrUndefined(data.patient_id),
      patient_identifier: StringOrNullOrUndefined(data.patient_identifier),
      doctor_id: NumberOrNullOrUndefined(data.doctor_id),
      note_description: StringOrNullOrUndefined(data.note_description),
      summary_note: StringOrNullOrUndefined(data.summary_note),
      birth_date: StringOrNullOrUndefined(data.birth_date),
      aba_supervisor: NumberOrNullOrUndefined(data.aba_supervisor),
      cpt_code: StringOrNullOrUndefined(data.cpt_code),
      diagnosis_code: StringOrNullOrUndefined(data.diagnosis_code),
      rendering_provider: NumberOrNullOrUndefined(data.rendering_provider),

      provider_signature: StringOrNullOrUndefined(data.provider_signature),
      provider_name: NumberOrNullOrUndefined(data.provider_name),
      provider_id: NumberOrNullOrUndefined(data.provider_id),
      provider_credential:
        data.provider_credential && data.provider_credential !== 'null'
          ? StringOrNullOrUndefined(data.provider_credential) ?? ''
          : '',

      supervisor_signature: StringOrNullOrUndefined(data.supervisor_signature),
      supervisor_name: NumberOrNullOrUndefined(data.supervisor_name),
      supervisor_id: NumberOrNullOrUndefined(data.supervisor_id),
      billed_blocked: Boolean(data.billed_blocked),
      billed: Boolean(data.billed),
      paid: Boolean(data.paid),
      bcba: StringOrNullOrUndefined(data.bcba),
      md: StringOrNullOrUndefined(data.md),
      md2: StringOrNullOrUndefined(data.md2),
      md3: StringOrNullOrUndefined(data.md3),
      modifiers: StringOrNullOrUndefined(data.modifiers),
      valid_modifiers: (data.valid_modifiers as { modifier: string; unit_price: number }[]) ?? undefined,
      meet_with_client_at: StringOrNullOrUndefined(data.meet_with_client_at) ?? '',
      pos_description: StringOrNullOrUndefined(data.pos_description),
      status: (StringOrNullOrUndefined(data.status) as NoteStatus) ?? 'pending',
      location_id: NumberOrNullOrUndefined(data.location_id),

      pos: StringOrNullOrUndefined(data.pos),
      pa_service_id: NumberOrNullOrUndefined(data.pa_service_id),
      is_telehealth: BooleanOrNullOrUndefined(data.is_telehealth),

      session_date: StringOrNullOrUndefined(data.session_date),
      time_in: StringOrNullOrUndefined(data.time_in),
      time_out: StringOrNullOrUndefined(data.time_out),
      time_in2: StringOrNullOrUndefined(data.time_in2),
      time_out2: StringOrNullOrUndefined(data.time_out2),
      session_length_total: NumberOrNullOrUndefined(data.session_length_total),
      total_hours: StringOrNullOrUndefined(data.total_hours),
      total_minutes: NumberOrNullOrUndefined(data.total_minutes),
      total_units: NumberOrNullOrUndefined(data.total_units),
      unit_price: NumberOrNullOrUndefined(data.unit_price),
      total_charges: NumberOrNullOrUndefined(data.total_charges),
      stored_total_units: NumberOrNullOrUndefined(data.stored_total_units),
      stored_unit_price: NumberOrNullOrUndefined(data.stored_unit_price),
      stored_total_charges: NumberOrNullOrUndefined(data.stored_total_charges),
      charges_complete: BooleanOrNullOrUndefined(data.charges_complete ?? data.charges_locked) ?? false,
      charge_status: NoteBcbaV2.resolveChargeStatus(data),
      configured_unit_price: NumberOrNullOrUndefined(data.configured_unit_price),
      charges_calculated_at: DateOrNullOrUndefined(
        data.charges_calculated_at
      ),
      charges_locked: Boolean(data.charges_locked),
      created_at: DateOrNullOrUndefined(data.created_at),
      updated_at: DateOrNullOrUndefined(data.updated_at),
      deleted_at: DateOrNullOrUndefined(data.deleted_at),
      insurance_id: NumberOrNullOrUndefined(data.insurance_id),

      // 97151
      subtype:
        (StringOrNullOrUndefined(data.subtype) as AssessmentToolType) ??
        'observation/parent interview',
      assessment_tools: data.assessment_tools,
      intake_outcome: data.intake_outcome,
      BCBA_conducted_client_observations: BooleanOrNullOrUndefined(
        data.BCBA_conducted_client_observations
      ),
      BCBA_conducted_assessments: BooleanOrNullOrUndefined(
        data.BCBA_conducted_assessments
      ),

      // 97155
      was_the_rbt_present: BooleanOrNullOrUndefined(data.was_the_rbt_present),
      maladaptives: MaladaptivesBuilder(data.maladaptives),
      replacements: ReplacementBuilder(data.replacements),
      rbt_training_goals: RbtTrainingGoalsBuilder(data.rbt_training_goals),
      replacement_protocols: ReplacementProtocolsBuilder(
        data.replacement_protocols
      ),
      intervention_protocols: InterventionProtocolsBuilder(
        data.intervention_protocols
      ),
      modifications_needed_at_this_time: BooleanOrNullOrUndefined(
        data.modifications_needed_at_this_time
      ),
      additional_goals_or_interventions: StringOrNullOrUndefined(
        data.additional_goals_or_interventions
      ),
      why_was_the_protocol_modified_or_not_modified: StringOrNullOrUndefined(
        data.why_was_the_protocol_modified_or_not_modified
      ),
      describe_in_detail_the_protocol_modifications: StringOrNullOrUndefined(
        data.describe_in_detail_the_protocol_modifications
      ),

      // 97156
      behavior_protocols: BehaviorProtocolsBuilder(data.behavior_protocols),
      caregiver_goals: CaregiverGoalsBuilder(data.caregiver_goals),
      teacher_goals: TeacherGoalsBuilder(data.teacher_goals),

      progress_noted_this_session_compared_to_previous_session: StringOrNullOrUndefined(
        data.progress_noted_this_session_compared_to_previous_session
      )
    };
    Object.assign(this, result);
  }

  private static resolveChargeStatus(data: Partial<NoteBcbaV2>): NoteChargeStatus {
    if (data.charge_status === 'ready' || data.charge_status === 'needs_recalculation' || data.charge_status === 'missing_configuration') {
      return data.charge_status;
    }

    return data.charges_complete || data.charges_locked ? 'ready' : 'needs_recalculation';
  }
}

export type Maladaptive = { plan_id: number | null; name: string; frequency: number };
export type Replacement = { plan_id: number | null; name: string; percentage: number};

export type CaregiverGoals = CaregiverGoalProtocol[];
export type TeacherGoals = TeacherGoalProtocol[];
export type RbtTrainingGoals = RbtGoalProtocol[];
export type ReplacementProtocols = PlanProtocol[];
export type InterventionProtocols = Protocol[];
export type BehaviorProtocols = DiscussedPlanProtocol[];
export type MaladaptivesData = Maladaptive[];
export type ReplacementsData = Replacement[];

const CaregiverGoalsBuilder = (data?: CaregiverGoalProtocol[]): CaregiverGoals =>
  data ?? [];
const TeacherGoalsBuilder = (data?: TeacherGoalProtocol[]): TeacherGoals =>
  data ?? [];
const RbtTrainingGoalsBuilder = (data?: RbtGoalProtocol[]): RbtTrainingGoals => data ?? [];
const ReplacementProtocolsBuilder = (
  data?: PlanProtocol[]
): ReplacementProtocols => data ?? [];
const InterventionProtocolsBuilder = (
  data?: Protocol[]
): InterventionProtocols => data ?? [];
const BehaviorProtocolsBuilder = (
  data?: DiscussedPlanProtocol[]
): BehaviorProtocols => data ?? [];
const MaladaptivesBuilder = (data?: Maladaptive[]): MaladaptivesData => data ?? [];
const ReplacementBuilder = (data?: Replacement[]): ReplacementsData => data ?? [];
export function isNoteBcbaV2(data: NoteRbtV2 | NoteBcbaV2 | NoteCombinedV2): data is NoteBcbaV2 {
  return data?.type === 'bcba';
}
